import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { emailPaths } from './routes/paths'
import { I18nProvider } from './i18n/I18nProvider'
import { SessionProvider, useSession } from './session/SessionProvider'
import { AppLayout } from './ui/Layout'
import { Busy } from './ui/controls'
import { ForgotPassword, ResetPassword, SignIn, SignUp, VerifyEmail } from './routes/auth'
import { Onboarding } from './routes/Onboarding'
import { ProfilePage } from './routes/ProfilePage'
import { PhotoPage } from './routes/PhotoPage'
import { ContactMethodsPage } from './routes/ContactMethodsPage'
import { PreferencesPage } from './routes/PreferencesPage'
import { AccountPage } from './routes/AccountPage'
import { MatchingPage } from './routes/MatchingPage'
import { MatchesPage } from './routes/MatchesPage'
import { MatchReportPage } from './routes/MatchReportPage'
import { InvitationsPage, InviteeViewPage } from './routes/InvitationsPage'
import { ConnectionPage } from './routes/ConnectionPage'
import { PaymentCancelled, PaymentSuccess } from './routes/payment'
import { AdminLayout, AdminPrices, AdminReports, AdminSettings } from './routes/admin/AdminPages'
import { AdminTwoFactorPage } from './routes/admin/TwoFactorPage'

/**
 * Routing, and the one decision routing makes: whether the caller has a session.
 *
 * `status` starts as `loading` rather than `signedOut`, and the difference matters — rendering
 * the sign-in page while the answer is still in flight would throw a signed-in user back to the
 * door on every refresh.
 */
/**
 * Exported so that a test can drive it: two of these paths are only ever reached by clicking a
 * link in an email, which means nothing in the application ever exercises them.
 */
export function SignedOutRoutes() {
  return (
    <Routes>
      <Route path="/signup" element={<SignUp />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path={emailPaths.verifyEmail} element={<VerifyEmail />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path={emailPaths.resetPassword} element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  )
}

function Router() {
  const { status } = useSession()

  if (status === 'loading') {
    return <Busy />
  }

  if (status === 'signedOut') {
    return <SignedOutRoutes />
  }

  return (
    <Routes>
      {/* Still reachable while signed in: someone can land here from a link in their inbox. */}
      <Route path={emailPaths.verifyEmail} element={<VerifyEmail />} />

      <Route element={<AppLayout />}>
        <Route path="/" element={<Onboarding />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/photo" element={<PhotoPage />} />
        <Route path="/contact" element={<ContactMethodsPage />} />
        <Route path="/preferences" element={<PreferencesPage />} />

        <Route path="/matching" element={<MatchingPage />} />
        <Route path="/matches" element={<MatchesPage />} />
        <Route path="/matches/:matchId" element={<MatchReportPage />} />
        <Route path="/invitations" element={<InvitationsPage />} />
        <Route path="/invitations/:invitationId" element={<InviteeViewPage />} />
        <Route path="/connection" element={<ConnectionPage />} />

        {/* Where Stripe returns the browser (Stripe:SuccessUrl / Stripe:CancelUrl). */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancelled" element={<PaymentCancelled />} />

        {/*
          The server decides who may reach these (安全架构 §21: role plus a second factor
          answered this session). Routing only decides what to draw.
        */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminReports />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="prices" element={<AdminPrices />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="two-factor" element={<AdminTwoFactorPage />} />
        </Route>

        <Route path="/account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <SessionProvider>
          <Router />
        </SessionProvider>
      </BrowserRouter>
    </I18nProvider>
  )
}
